import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Avatar from '@mui/material/Avatar'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import PaymentsIcon from '@mui/icons-material/Payments'

const layerFields = ['layer_one', 'layer_two', 'layer_three', 'layer_four', 'layer_five']

const getRouteLayers = (route) => layerFields.map((field) => route?.[field]).filter(Boolean)

const formatAmount = (value) => {
  if (value === null || value === '' || Number.isNaN(Number(value))) {
    return null
  }

  return `RM ${parseFloat(value).toFixed(2)}`
}

const getAmountLabel = (claimRange, index, total) => {
  const from = formatAmount(claimRange?.amount_from)
  const to = formatAmount(claimRange?.amount_to)

  if (index === total - 1 || claimRange?.amount_to === null) {
    return `${from || 'This amount'} and above`
  }

  return `${from || 'RM 0.00'} to ${to || 'the next tier'}`
}

const getInitials = (value) => {
  if (!value) return ''

  const words = String(value).trim().split(' ').filter(Boolean)
  if (words.length === 1) return words[0].charAt(0).toUpperCase()

  return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase()
}

const getMembers = (members = []) => members.filter((member) => member?.name)
const getMemberNames = (members = []) => getMembers(members).map((member) => member.name)

const getLayerDetail = (layer) => {
  if (layer?.auto_generated && layer?.name === 'Direct Reporting Manager') {
    return {
      members: [],
      names: [],
      helper: "Resolves to the submitter's direct reporting manager",
      badge: 'Dynamic',
    }
  }

  if (layer?.auto_generated && layer?.name === 'Head of Department') {
    return {
      members: [],
      names: [],
      helper: "Resolves to the submitter's department head",
      badge: 'Dynamic',
    }
  }

  return {
    members: getMembers(layer?.members),
    names: getMemberNames(layer?.members),
    helper: null,
    badge: null,
  }
}

const NameList = ({ names = [], helper }) => {
  if (helper) {
    return (
      <Typography variant="body2" color="light-gray-text.main">
        {helper}
      </Typography>
    )
  }

  if (!names.length) {
    return (
      <Typography variant="body2" color="light-gray-text.main">
        No members configured
      </Typography>
    )
  }

  const visibleNames = names.slice(0, 2)
  const remaining = names.length - visibleNames.length

  return (
    <Box display="flex" flexWrap="wrap" gap={0.75}>
      {visibleNames.map((name) => (
        <Box
          key={name}
          px={1}
          py={0.25}
          border={1}
          borderColor="gray-line.main"
          borderRadius="4.5px"
          bgcolor="white.main"
        >
          <Typography variant="body2" color="dark-text.main">
            {name}
          </Typography>
        </Box>
      ))}
      {remaining > 0 && (
        <Tooltip
          title={names.slice(2).map((name) => (
            <Box key={name}>{name}</Box>
          ))}
        >
          <Box
            px={1}
            py={0.25}
            border={1}
            borderColor="gray-line.main"
            borderRadius="4.5px"
            bgcolor="white.main"
            sx={{ cursor: 'default' }}
          >
            <Typography variant="body2" color="light-gray-text.main">
              +{remaining} more
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}

const StepMarker = ({ type, label, names = [], members = [] }) => {
  const iconSx = { fontSize: 22, color: 'white.main' }
  const firstMember = members[0]
  const markerColor = type === 'submitter' ? 'alternate.main' : 'alternate.light'
  const markerTextColor = type === 'submitter' ? 'white.main' : 'alternate.main'
  const remainingMembers = Math.max(members.length - 1, 0)

  return (
    <Box
      width={48}
      height={48}
      borderRadius="50%"
      bgcolor={markerColor}
      display="flex"
      alignItems="center"
      justifyContent="center"
      border={3}
      borderColor={type === 'submitter' ? 'alternate.main' : 'white.main'}
      flexShrink={0}
      position="relative"
      boxShadow={type === 'submitter' ? 'none' : '0 0 0 1px rgba(0, 0, 0, 0.08)'}
    >
      {type === 'submitter' && <PersonOutlineIcon sx={iconSx} />}
      {type === 'approval' &&
        (firstMember?.profile_image_url ? (
          <Avatar
            src={firstMember.profile_image_url}
            alt={firstMember.name}
            sx={{ width: 48, height: 48 }}
          />
        ) : names.length === 1 ? (
          <Typography variant="body1" color={markerTextColor} fontWeight="bold">
            {getInitials(names[0])}
          </Typography>
        ) : (
          <AccountTreeIcon sx={{ ...iconSx, color: markerTextColor }} />
        ))}
      {type === 'reimbursement' &&
        (firstMember?.profile_image_url ? (
          <Avatar
            src={firstMember.profile_image_url}
            alt={firstMember.name}
            sx={{ width: 48, height: 48 }}
          />
        ) : (
          <PaymentsIcon sx={{ ...iconSx, color: markerTextColor }} />
        ))}
      {remainingMembers > 0 && (
        <Box
          width={34}
          height={34}
          borderRadius="50%"
          bgcolor="gray-line.main"
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="absolute"
          right={-9}
          bottom={-9}
          border={2}
          borderColor="white.main"
        >
          <Typography variant="body2" color="light-gray-text.main" fontWeight="bold">
            {remainingMembers}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

const JourneyStep = ({ type, title, subtitle, names, members, helper, badge, isLast }) => (
  <Box display="flex" gap={1.5} position="relative">
    <Box display="flex" flexDirection="column" alignItems="center">
      <StepMarker type={type} label={title} names={names} members={members} />
      {!isLast && <Box width={3} flex={1} minHeight={28} bgcolor="gray-line.main" />}
    </Box>
    <Box flex={1} pb={isLast ? 0 : 2.25} minWidth={0}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="body1" color="dark-text.main" fontWeight="bold">
          {title}
        </Typography>
        {badge && (
          <Box px={0.75} py={0.15} borderRadius="4.5px" bgcolor="warning.light">
            <Typography variant="caption" color="warning.dark" fontWeight="bold">
              {badge}
            </Typography>
          </Box>
        )}
      </Box>
      {subtitle && (
        <Typography variant="body2" color="light-gray-text.main" mb={0.75}>
          {subtitle}
        </Typography>
      )}
      <NameList names={names} helper={helper} />
    </Box>
  </Box>
)

const ClaimApprovalPreview = ({
  claimRange,
  index = 0,
  total = 1,
  approvalRoutes = [],
  reimbursementAdmins = [],
  compact = false,
}) => {
  const route = approvalRoutes.find(
    (approvalRoute) => String(approvalRoute.id) === String(claimRange?.approval_route_id)
  )
  const layers = getRouteLayers(route)
  const reimbursementMembers = getMembers(reimbursementAdmins)
  const reimbursementNames = getMemberNames(reimbursementAdmins)
  const steps = [
    {
      type: 'submitter',
      title: 'Staff submits claim',
      subtitle: `Claims ${getAmountLabel(claimRange, index, total)}`,
      names: [],
      helper: 'The claim amount selects this claim rule.',
    },
    ...layers.map((layer, layerIndex) => {
      const detail = getLayerDetail(layer)

      return {
        type: 'approval',
        title: layer?.name || `Approval step ${layerIndex + 1}`,
        subtitle: `Approval chain step ${layerIndex + 1}`,
        members: detail.members,
        names: detail.names,
        helper: detail.helper,
        badge: detail.badge,
      }
    }),
    {
      type: 'reimbursement',
      title: 'Reimbursement admins',
      subtitle: 'From Admin Permissions > Claim reimbursement',
      members: reimbursementMembers,
      names: reimbursementNames,
      helper: reimbursementNames.length
        ? null
        : 'No reimbursement admins configured. Add users with Claim reimbursement permission.',
    },
  ]

  return (
    <Box
      border={1}
      borderColor="gray-line.main"
      borderRadius="4.5px"
      bgcolor="white.main"
      p={compact ? 2 : 3}
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={0.5}>
        <Typography variant="body2" color="light-gray-text.main" sx={{ ml: { md: 'auto' } }}>
          Uses {route?.name || 'selected approval chain'}
        </Typography>
      </Box>

      <Box display="flex" flexDirection="column">
        {steps.map((step, stepIndex) => (
          <JourneyStep
            key={`${step.type}-${step.title}-${stepIndex}`}
            {...step}
            isLast={stepIndex === steps.length - 1}
          />
        ))}
      </Box>
    </Box>
  )
}

export default ClaimApprovalPreview
